/**
 * BulkActionsBar Component
 *
 * A generic, reusable bulk actions bar for selected items.
 * Supports any entity type with configurable actions.
 * Includes Framer Motion animations and accessibility features.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { BulkActionsBarProps, BulkActionItem, BulkActionVariant, isDropdownAction } from './types';

// ============================================
// Variant Styles
// ============================================

const variantStyles: Record<BulkActionVariant, string> = {
  default: 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50',
  primary: 'border-purple-300 text-purple-700 bg-white hover:bg-purple-50',
  success: 'border-green-300 text-green-700 bg-white hover:bg-green-50',
  warning: 'border-yellow-300 text-yellow-700 bg-white hover:bg-yellow-50',
  danger: 'border-red-300 text-red-700 bg-white hover:bg-red-50',
};

const themeStyles: Record<string, string> = {
  purple: 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200',
  blue: 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200',
  green: 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200',
  gray: 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200',
};

const themeTextStyles: Record<string, string> = {
  purple: 'text-purple-900',
  blue: 'text-blue-900',
  green: 'text-green-900',
  gray: 'text-gray-900',
};

const themeLinkStyles: Record<string, string> = {
  purple: 'text-purple-600 hover:text-purple-800',
  blue: 'text-blue-600 hover:text-blue-800',
  green: 'text-green-600 hover:text-green-800',
  gray: 'text-gray-600 hover:text-gray-800',
};

// ============================================
// Component
// ============================================

export function BulkActionsBar<T>({
  selectedIds,
  items,
  getItemId,
  actions,
  onClearSelection,
  entityName = 'item',
  className = '',
  theme = 'purple',
}: BulkActionsBarProps<T>) {
  const shouldReduceMotion = useReducedMotion();
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingActionId, setProcessingActionId] = useState<string | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [confirmingAction, setConfirmingAction] = useState<{
    action: BulkActionItem<T>;
    optionValue?: string;
  } | null>(null);

  // Get selected items for conditional rendering
  const selectedItems = useMemo(() => {
    return items.filter(item => selectedIds.has(getItemId(item)));
  }, [items, selectedIds, getItemId]);

  // Animation variants
  const barVariants = shouldReduceMotion
    ? undefined
    : {
        initial: { opacity: 0, y: 50 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: 50 },
      };

  const menuVariants = shouldReduceMotion
    ? undefined
    : {
        initial: { opacity: 0, scale: 0.95 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.95 },
      };

  // Handle action execution
  const executeAction = useCallback(async (
    action: BulkActionItem<T>,
    optionValue?: string
  ) => {
    setIsProcessing(true);
    setProcessingActionId(action.id);
    setConfirmingAction(null);
    setOpenDropdownId(null);

    try {
      if (isDropdownAction(action) && optionValue) {
        await action.onClick(Array.from(selectedIds), optionValue);
      } else if (!isDropdownAction(action)) {
        await action.onClick(Array.from(selectedIds));
      }
    } catch (error) {
      console.error(`Bulk action ${action.id} failed:`, error);
    } finally {
      setIsProcessing(false);
      setProcessingActionId(null);
    }
  }, [selectedIds]);

  // Handle action click (with confirmation if needed)
  const handleActionClick = useCallback((
    action: BulkActionItem<T>,
    optionValue?: string
  ) => {
    if (action.confirm) {
      setConfirmingAction({ action, optionValue });
    } else {
      executeAction(action, optionValue);
    }
  }, [executeAction]);

  // Render individual action button
  const renderActionButton = (action: BulkActionItem<T>) => {
    const variant = action.variant || 'default';
    const isDisabled = isProcessing || (action.disabled?.(selectedItems) ?? false);
    const isVisible = action.show?.(selectedItems) ?? true;

    if (!isVisible) return null;

    // Dropdown action
    if (isDropdownAction(action)) {
      return (
        <div key={action.id} className="relative">
          <motion.button
            onClick={() => setOpenDropdownId(openDropdownId === action.id ? null : action.id)}
            disabled={isDisabled}
            className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium disabled:opacity-50 ${variantStyles[variant]}`}
            whileHover={shouldReduceMotion ? undefined : { scale: 1.05 }}
            whileTap={shouldReduceMotion ? undefined : { scale: 0.95 }}
            title={isDisabled && action.disabledTooltip ? action.disabledTooltip : action.label}
          >
            <span className="h-4 w-4 mr-1.5">{action.icon}</span>
            {action.label}
          </motion.button>

          <AnimatePresence>
            {openDropdownId === action.id && (
              <motion.div
                variants={menuVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.15 }}
                className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20"
              >
                <div className="py-1" role="menu">
                  {action.options.map(option => (
                    <motion.button
                      key={option.value}
                      onClick={() => handleActionClick(action, option.value)}
                      className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-purple-50"
                      role="menuitem"
                      whileHover={shouldReduceMotion ? undefined : { x: 4, backgroundColor: 'rgb(250 245 255)' }}
                    >
                      {option.icon && <span className="h-4 w-4 mr-2">{option.icon}</span>}
                      {option.label}
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    }

    // Regular action button
    return (
      <motion.button
        key={action.id}
        onClick={() => handleActionClick(action)}
        disabled={isDisabled}
        className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium disabled:opacity-50 ${variantStyles[variant]}`}
        whileHover={shouldReduceMotion ? undefined : { scale: 1.05 }}
        whileTap={shouldReduceMotion ? undefined : { scale: 0.95 }}
        title={isDisabled && action.disabledTooltip ? action.disabledTooltip : action.label}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
      >
        <span className="h-4 w-4 mr-1.5">{action.icon}</span>
        {action.label}
        {processingActionId === action.id && (
          <motion.svg
            className="h-4 w-4 ml-2"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </motion.svg>
        )}
      </motion.button>
    );
  };

  const count = selectedIds.size;
  const entityLabel = count === 1 ? entityName : `${entityName}s`;

  return (
    <>
      <motion.div
        variants={barVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className={`border rounded-lg p-4 flex items-center justify-between shadow-md ${themeStyles[theme]} ${className}`}
      >
        {/* Left side: Selection info */}
        <div className="flex items-center space-x-4">
          <motion.span
            initial={shouldReduceMotion ? undefined : { scale: 0 }}
            animate={shouldReduceMotion ? undefined : { scale: 1 }}
            className={`text-sm font-medium ${themeTextStyles[theme]}`}
          >
            {count} {entityLabel} selected
          </motion.span>
          <motion.button
            onClick={onClearSelection}
            className={`text-sm font-medium ${themeLinkStyles[theme]}`}
            disabled={isProcessing}
            whileHover={shouldReduceMotion ? undefined : { scale: 1.05 }}
            whileTap={shouldReduceMotion ? undefined : { scale: 0.95 }}
          >
            Clear Selection
          </motion.button>
        </div>

        {/* Right side: Action buttons */}
        <div className="flex items-center space-x-2">
          <AnimatePresence mode="popLayout">
            {actions.map(action => renderActionButton(action))}
          </AnimatePresence>

          {/* Global processing indicator */}
          <AnimatePresence>
            {isProcessing && !processingActionId && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className={`flex items-center text-sm ${themeLinkStyles[theme]}`}
              >
                <motion.svg
                  className="h-4 w-4 mr-2"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </motion.svg>
                Processing...
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Confirmation Modal */}
      <AnimatePresence>
        {confirmingAction && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => setConfirmingAction(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {confirmingAction.action.confirm?.title || 'Confirm Action'}
                </h3>
                <button
                  onClick={() => setConfirmingAction(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <p className="text-gray-600 mb-6">
                {confirmingAction.action.confirm?.message || `Are you sure you want to perform this action on ${count} ${entityLabel}?`}
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setConfirmingAction(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  {confirmingAction.action.confirm?.cancelText || 'Cancel'}
                </button>
                <button
                  onClick={() => executeAction(confirmingAction.action, confirmingAction.optionValue)}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700"
                >
                  {confirmingAction.action.confirm?.confirmText || 'Confirm'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default BulkActionsBar;
